import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Box, Alert, Tab, Tabs } from '@mui/material';

import { DetailsCard } from 'ui/components/Page/DetailsCard';
import { ConfirmationModal } from 'ui/components/Modal/ConfirmationModal';
import { TabPanel } from 'ui/components/TabPanel';
import Accordion from 'ui/components/Accordion/Accordion';
import {
  TransferOrderFormData,
  TransferOrder,
  TransferOrderTab,
} from 'services/transferOrders/types';
import { Location } from 'services/locations';
import { showNotification } from 'services/api';

import { useTransferOrderType } from '../TransferOrderForm/hooks/useTransferOrderType';
import { useTransferOrderNumber } from '../TransferOrderForm/hooks/useTransferOrderNumber';
import { useTransferOrderActions } from '../TransferOrderForm/hooks/useTransferOrderActions';
import { transferOrderValidationSchema } from '../TransferOrderForm/validations';
import { TransferOrderTitleBar } from '../TransferOrderTitleBar';
import { fetchTransferOrder } from 'services/transferOrders/api';
import { logErrorCtx } from 'app/logging';
import TransferOrderGeneralTab from './components/GeneralTab/TransferOrderGeneralTab';

interface TransferOrderDetailsCardProps {
  transferOrderId: number | null;
  onClose: () => void;
  onSuccess?: (transferOrder: TransferOrder) => void;
  bypassRouteLeavingGuard: boolean;
  setBypassRouteLeavingGuard: React.Dispatch<React.SetStateAction<boolean>>;
}

const initialFormData: TransferOrderFormData = {
  sourceLocationId: null,
  destinationLocationId: null,
  transferOrderNumber: null,
  transferReason: '',
  items: [],
  isEditing: false,
  isDirty: false,
};

const TransferOrderDetailsCard: React.FC<TransferOrderDetailsCardProps> = ({
  transferOrderId,
  onClose,
  onSuccess,
  bypassRouteLeavingGuard,
  setBypassRouteLeavingGuard,
}) => {
  const [formData, setFormData] =
    useState<TransferOrderFormData>(initialFormData);
  const [_initialFormState, setInitialFormState] =
    useState<TransferOrderFormData>(initialFormData);
  const oldFormState = useRef<TransferOrderFormData>(initialFormData);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(TransferOrderTab.General);

  const isNewTransferOrder = transferOrderId === -1;

  // Hooks for transfer order logic
  const { transferType, isLocationSelectionComplete } = useTransferOrderType({
    sourceLocationId: formData.sourceLocationId,
    destinationLocationId: formData.destinationLocationId,
  });

  const { orderNumber, numericOrderNumber, isGenerating } =
    useTransferOrderNumber({
      sourceLocationId: formData.sourceLocationId,
      destinationLocationId: formData.destinationLocationId,
      isLocationSelectionComplete,
      transferType,
    });

  const handleSuccess = useCallback(
    (message: string, transferOrder?: TransferOrder, shouldClose?: boolean) => {
      // Show success toast
      showNotification(message, { variant: 'success' });

      // Reset the form state to mark as clean after successful save
      setFormData((prev) => {
        const updatedFormData = { ...prev, isDirty: false };
        // Update baseline to current state to prevent stale closure
        setInitialFormState(updatedFormData);
        return updatedFormData;
      });

      if (transferOrder && onSuccess) {
        onSuccess(transferOrder);
      }

      // If this is a "Save and Close" operation, close after setting bypass flag
      if (shouldClose) {
        setBypassRouteLeavingGuard(true);
        onClose();
      }
    },
    [onSuccess, setBypassRouteLeavingGuard, onClose]
  );

  const handleError = useCallback((message: string) => {
    showNotification(message, { variant: 'error' });
  }, []);

  const handleSaveAction = useCallback(
    async (_shouldClose: boolean = false) => {
      // Update oldState to current state before save (like ItemsPage does)
      // This prevents the DetailsCard from detecting changes during the save process
      oldFormState.current = formData;

      // The bypass flag will be set in handleSuccess after successful save
      // This prevents premature bypassing before the save operation completes

      // The actual save will be handled by the action handlers
      return true;
    },
    [formData]
  );

  // Validation function
  const validateForm = useCallback(async (): Promise<boolean> => {
    try {
      await transferOrderValidationSchema.validate(formData, {
        abortEarly: false,
      });
      setValidationErrors({});
      return true;
    } catch (error: any) {
      if (error.inner) {
        const errors: Record<string, string> = {};
        error.inner.forEach((err: any) => {
          if (err.path) {
            errors[err.path] = err.message;
          }
        });
        setValidationErrors(errors);
      }
      return false;
    }
  }, [formData]);

  const {
    handleSave: originalHandleSave,
    handleSaveAndClose: originalHandleSaveAndClose,
    handleIssue,
    handleQuickFulfill,
    handleDelete,
    isProcessing,
    canCreate,
  } = useTransferOrderActions({
    formData,
    onSuccess: handleSuccess,
    onError: handleError,
    onClose,
    validateForm,
  });

  // Wrap the save handlers to call handleSaveAction first
  const handleSave = useCallback(async () => {
    await handleSaveAction(false);
    return originalHandleSave();
  }, [handleSaveAction, originalHandleSave]);

  const handleSaveAndClose = useCallback(async () => {
    await handleSaveAction(true);
    return originalHandleSaveAndClose();
  }, [handleSaveAction, originalHandleSaveAndClose]);

  // Form field handlers
  const handleSourceLocationChange = useCallback(
    (location: Location | null) => {
      setFormData((prev) => ({
        ...prev,
        sourceLocationId: location?.id ?? null,
        isDirty: true,
      }));
      setValidationErrors((prev) => ({ ...prev, sourceLocationId: '' }));
    },
    []
  );

  const handleDestinationLocationChange = useCallback(
    (location: Location | null) => {
      setFormData((prev) => ({
        ...prev,
        destinationLocationId: location?.id ?? null,
        isDirty: true,
      }));
      setValidationErrors((prev) => ({ ...prev, destinationLocationId: '' }));
    },
    []
  );

  const handleTransferReasonChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prev) => ({
        ...prev,
        transferReason: event.target.value,
        isDirty: true,
      }));
      // Clear validation error when user starts typing
      setValidationErrors((prev) => ({ ...prev, transferReason: '' }));
    },
    []
  );

  const handleTransferOrderNumberChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prev) => ({
        ...prev,
        transferOrderNumber: event.target.value,
        isDirty: true,
      }));
      // Clear validation error when user starts typing
      setValidationErrors((prev) => ({ ...prev, transferOrderNumber: '' }));
    },
    []
  );

  const handleDeleteClick = useCallback(() => {
    if (formData.isEditing) {
      setShowDeleteConfirm(true);
    } else {
      handleDelete();
    }
  }, [formData.isEditing, handleDelete]);

  // Load transfer order data if editing
  const loadTransferOrder = useCallback(async () => {
    if (transferOrderId != null && transferOrderId !== -1) {
      setIsLoading(true);
      try {
        const transferOrder = await fetchTransferOrder(transferOrderId);
        if (transferOrder) {
          const loadedFormData = {
            sourceLocationId: transferOrder.sourceLocationId,
            destinationLocationId: transferOrder.destinationLocationId,
            transferOrderNumber: transferOrder.number,
            transferReason: transferOrder.transferReason || '',
            items: [],
            isEditing: true,
            isDirty: false,
          };
          setFormData(loadedFormData);
          setInitialFormState(loadedFormData); // Set this as the baseline for change detection
        } else {
          handleError('Transfer order not found');
          onClose();
        }
      } catch (error) {
        const err = error as Error;
        logErrorCtx('Error loading transfer order', {
          stackTrace: err.stack,
          error: err,
          description: `Failed to load transfer order ID: ${transferOrderId}`,
          component: 'TransferOrderDetailsCard',
        });
        handleError('Failed to load transfer order');
        onClose();
      } finally {
        setIsLoading(false);
      }
    } else {
      const newFormData = { ...initialFormData, isEditing: false };
      setFormData(newFormData);
      setInitialFormState(newFormData); // Set this as the baseline for change detection
    }
  }, [transferOrderId, handleError, onClose]);

  useEffect(() => {
    loadTransferOrder();
  }, [loadTransferOrder]);

  // Update the form's transferOrderNumber when numericOrderNumber changes (for new orders)
  useEffect(() => {
    if (
      !formData.isEditing &&
      numericOrderNumber &&
      numericOrderNumber !== 'XXXX' &&
      !formData.transferOrderNumber
    ) {
      setFormData((prev) => ({
        ...prev,
        transferOrderNumber: numericOrderNumber,
      }));
    }
  }, [numericOrderNumber, formData.isEditing, formData.transferOrderNumber]);

  const handleFormSubmit = async (e?: React.FormEvent): Promise<boolean> => {
    e?.preventDefault();

    try {
      // When called from the unsaved changes prompt, perform a "Save and Close" operation
      await handleSaveAndClose();
      return true;
    } catch (error) {
      // If save fails, return false to prevent the modal from closing
      return false;
    }
  };

  const handleActiveTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: TransferOrderTab) => {
      setActiveTab(newValue);
    },
    []
  );

  const isDraft = !formData.isEditing; // New transfer orders are always draft

  return (
    <DetailsCard
      isLoading={isLoading}
      onSubmit={handleFormSubmit}
      state={formData}
      oldState={oldFormState}
      bypassRouteLeavingGuard={bypassRouteLeavingGuard}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <TransferOrderTitleBar
          orderNumber={orderNumber}
          isGenerating={isGenerating}
          isNewTransferOrder={isNewTransferOrder}
          isProcessing={isProcessing}
          canCreate={canCreate}
          canDelete={formData.isEditing}
          onSave={handleSave}
          onSaveAndClose={handleSaveAndClose}
          onIssue={handleIssue}
          onQuickFulfill={handleQuickFulfill}
          onDelete={handleDeleteClick}
          onClose={onClose}
        />

        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          {activeTab === TransferOrderTab.General ? (
            <Accordion
              header={
                <Tabs
                  value={activeTab}
                  onChange={handleActiveTabChange}
                  indicatorColor="primary"
                  className={'redesign'}
                  sx={{
                    border: 'none !important',
                  }}
                >
                  <Tab
                    value={TransferOrderTab.General}
                    label="General"
                    data-qa="transfer-order-general-tab"
                  />
                  <Tab
                    value={TransferOrderTab.Attachments}
                    label="Attachments"
                    data-qa="transfer-order-attachments-tab"
                  />
                  <Tab
                    value={TransferOrderTab.Notes}
                    label="Notes"
                    data-qa="transfer-order-notes-tab"
                  />
                </Tabs>
              }
              details={
                <TransferOrderGeneralTab
                  formData={formData}
                  validationErrors={validationErrors}
                  isDraft={isDraft}
                  onSourceLocationChange={handleSourceLocationChange}
                  onDestinationLocationChange={handleDestinationLocationChange}
                  onTransferOrderNumberChange={handleTransferOrderNumberChange}
                  onTransferReasonChange={handleTransferReasonChange}
                />
              }
              expand={true}
            />
          ) : (
            <>
              <Tabs
                value={activeTab}
                onChange={handleActiveTabChange}
                indicatorColor="primary"
                className={'redesign'}
                sx={{
                  border: 'none !important',
                  borderBottom: '1px solid #e0e0e0',
                }}
              >
                <Tab
                  value={TransferOrderTab.General}
                  label="General"
                  data-qa="transfer-order-general-tab"
                />
                <Tab
                  value={TransferOrderTab.Attachments}
                  label="Attachments"
                  data-qa="transfer-order-attachments-tab"
                />
                <Tab
                  value={TransferOrderTab.Notes}
                  label="Notes"
                  data-qa="transfer-order-notes-tab"
                />
              </Tabs>

              {/* Attachments Tab */}
              <TabPanel
                value={activeTab}
                index={TransferOrderTab.Attachments}
                noSpacing
              >
                <Box sx={{ p: 3 }}>
                  <Alert severity="info">
                    Attachments functionality will be implemented in the next
                    iteration.
                  </Alert>
                </Box>
              </TabPanel>

              {/* Notes Tab */}
              <TabPanel
                value={activeTab}
                index={TransferOrderTab.Notes}
                noSpacing
              >
                <Box sx={{ p: 3 }}>
                  <Alert severity="info">
                    Notes functionality will be implemented in the next
                    iteration.
                  </Alert>
                </Box>
              </TabPanel>
            </>
          )}
        </Box>
      </Box>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onCancelClicked={() => setShowDeleteConfirm(false)}
        onConfirmClicked={() => {
          setShowDeleteConfirm(false);
          handleDelete();
        }}
        title="Delete Transfer Order"
        body="Are you sure you want to delete this transfer order? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </DetailsCard>
  );
};

export default TransferOrderDetailsCard;
